const {
    ACTIVE,
    IN_ACTIVE,
    SUNSET,
    PLANNED,
    enumInventoryStatuses,
} = require("../consts/inventoryStatus");

const EADIN_BUY_MONTHS = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);

/** Product lifecycle labels for Upcoming Buys status column. */
const enumUpcomingBuyProductStatus = enumInventoryStatuses;

function parseIsoDate(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    let normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    if (!normalized.includes("T")) {
        normalized = `${normalized}T00:00:00`;
    }
    if (!/[zZ]$/.test(normalized)) {
        normalized = `${normalized}Z`;
    }

    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getMonthKey(value) {
    const parsed = parseIsoDate(value);
    if (!parsed) return null;
    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function isEadinBuyMonth(monthKey) {
    const month = Number(monthKey?.slice(5, 7));
    return EADIN_BUY_MONTHS.has(month);
}

/** Map any PO date to the next Eadin buy window (Mar/Jun/Dec) on or after that month. */
function resolveEadinBuyMonthKey(value) {
    const parsed = parseIsoDate(value);
    if (!parsed) return null;

    const year = parsed.getUTCFullYear();
    const month = parsed.getUTCMonth() + 1;

    for (const eadinMonth of EADIN_BUY_MONTHS) {
        if (month <= eadinMonth) {
            return `${year}-${String(eadinMonth).padStart(2, "0")}`;
        }
    }

    return `${year + 1}-03`;
}

function formatUpcomingBuyDate(value) {
    const parsed = parseIsoDate(value);
    if (!parsed) return "—";
    return parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatUpcomingBuyMonthLabel(monthKey) {
    const parsed = parseIsoDate(`${monthKey}-01`);
    if (!parsed) return monthKey;
    return parsed.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}

function formatUpcomingBuyCurrency(value) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);
}

function formatUpcomingBuyNumber(value) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value) || 0);
}

const UPCOMING_BUY_STATUS_BADGE_BASE =
    "inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md px-2 py-1 text-[11px] font-medium shadow-none";

function normalizeUpcomingBuyStatusKey(status) {
    const raw = String(status || "").trim();
    if (!raw) return "";
    if (enumUpcomingBuyProductStatus[raw]) return raw;

    const fromLabel = Object.entries(enumUpcomingBuyProductStatus).find(([, label]) => label === raw);
    if (fromLabel) return fromLabel[0];

    const upper = raw.toUpperCase();
    if (upper === "INACTIVE") return IN_ACTIVE;
    if (enumUpcomingBuyProductStatus[upper]) return upper;

    return raw;
}

function getUpcomingBuyStatusLabel(status) {
    const key = normalizeUpcomingBuyStatusKey(status);
    if (!key) return "—";
    return enumUpcomingBuyProductStatus[key] || status;
}

function getUpcomingBuyStatusTone(status) {
    const key = normalizeUpcomingBuyStatusKey(status);

    if (key === ACTIVE) {
        return `${UPCOMING_BUY_STATUS_BADGE_BASE} border-transparent bg-primary text-white`;
    }
    if (key === IN_ACTIVE) {
        return `${UPCOMING_BUY_STATUS_BADGE_BASE} border-transparent bg-slate-100 text-slate-700`;
    }
    if (key === SUNSET) {
        return `${UPCOMING_BUY_STATUS_BADGE_BASE} border-transparent bg-amber-50 text-amber-700`;
    }
    if (key === PLANNED) {
        return `${UPCOMING_BUY_STATUS_BADGE_BASE} border-transparent bg-blue-50 text-blue-700`;
    }

    return `${UPCOMING_BUY_STATUS_BADGE_BASE} border border-border bg-white text-foreground`;
}

function getBuyOverrideTone(value) {
    if (value === "exclude") {
        return "border-transparent bg-rose-50 text-rose-700";
    }
    return "border-transparent bg-emerald-50 text-emerald-700";
}

function addDaysToIsoDate(value, days) {
    const parsed = parseIsoDate(value);
    if (!parsed) return null;
    const next = new Date(parsed.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next.toISOString().slice(0, 10);
}

function csvEscape(value) {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes("\n") || text.includes('"')) {
        return `"${text.replaceAll('"', '""')}"`;
    }
    return text;
}

function escapeHtml(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function slugifyUpcomingBuySupplier(value) {
    return value.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replaceAll(/^-+|-+$/g, "") || "supplier";
}

function downloadUpcomingBuysCsv(filename, rows) {
    const header = [
        "Supply SKU",
        "Product Name",
        "Supplier",
        "Buy Units",
        "Units To Order",
        "Unit Cost",
        "Order Value",
        "Deposit Liability",
        "Lead Time Days",
        "PO Planned Date",
        "Expected Arrival Date",
    ];

    const body = rows.map((row) => [
        row.supplySku,
        row.productName,
        row.supplier,
        row.buyUnits,
        row.unitsToOrder,
        formatUpcomingBuyCurrency(row.unitCost),
        formatUpcomingBuyCurrency(row.orderValue),
        formatUpcomingBuyCurrency(row.depositLiability),
        row.leadTimeDays,
        formatUpcomingBuyDate(row.poPlannedDate),
        formatUpcomingBuyDate(row.expectedArrivalDate),
    ]);

    const csv = [header, ...body].map((columns) => columns.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function downloadUpcomingBuysPdf(supplierName, selectedMonthLabel, rows) {
    const printWindow = window.open("", "_blank", "width=1080,height=780");
    if (!printWindow) return;

    const safeSupplierName = escapeHtml(supplierName);
    const safeMonthLabel = escapeHtml(selectedMonthLabel);
    const generatedDate = escapeHtml(
        new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    );
    const totalUnitsToOrder = formatUpcomingBuyNumber(rows.reduce((sum, row) => sum + Number(row.unitsToOrder || 0), 0));
    const totalOrderValue = formatUpcomingBuyCurrency(rows.reduce((sum, row) => sum + Number(row.orderValue || 0), 0));

    const tableRows = rows
        .map(
            (row) => `
    <tr>
      <td>${escapeHtml(row.supplySku)}</td>
      <td>${escapeHtml(row.productName)}</td>
      <td class="num">${escapeHtml(formatUpcomingBuyNumber(row.unitsToOrder))}</td>
      <td class="num">${escapeHtml(formatUpcomingBuyCurrency(row.unitCost))}</td>
      <td class="num">${escapeHtml(formatUpcomingBuyCurrency(row.orderValue))}</td>
      <td>${escapeHtml(formatUpcomingBuyDate(row.poPlannedDate))}</td>
      <td>${escapeHtml(formatUpcomingBuyDate(row.expectedArrivalDate))}</td>
    </tr>
  `
        )
        .join("");

    const html = `<!doctype html>
  <html lang="en">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${safeSupplierName} Upcoming Buys</title>
      <style>
        @page { size: landscape; margin: 16mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 0; color: #171717; background: #ffffff; }
        .page { padding: 0; }
        .header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; margin-bottom: 24px; }
        .eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.14em; color: #737373; margin-bottom: 8px; }
        h1 { font-size: 24px; margin: 0 0 6px; }
        p { margin: 0; color: #525252; }
        .metrics { display: flex; gap: 12px; flex-wrap: wrap; margin: 20px 0 24px; }
        .metric { border: 1px solid #e5e5e5; border-radius: 10px; padding: 12px 14px; min-width: 160px; }
        .metric-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: #737373; }
        .metric-value { margin-top: 6px; font-size: 18px; font-weight: 600; color: #171717; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: #737373; padding: 10px 8px; border-bottom: 1px solid #d4d4d4; }
        tbody td { padding: 10px 8px; border-bottom: 1px solid #f0f0f0; font-size: 12px; vertical-align: top; word-break: break-word; }
        tbody tr:nth-child(even) { background: #fafafa; }
        .num { text-align: right; white-space: nowrap; }
        .col-sku { width: 12%; }
        .col-product { width: 24%; }
        .col-units { width: 12%; }
        .col-cost { width: 12%; }
        .col-value { width: 14%; }
        .col-po { width: 13%; }
        .col-arrival { width: 13%; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <main class="page">
        <div class="header">
          <div>
            <div class="eyebrow">Upcoming Buys · Supplier Export</div>
            <h1>${safeSupplierName}</h1>
            <p>${safeMonthLabel}</p>
          </div>
          <div>
            <div class="eyebrow">Generated</div>
            <p>${generatedDate}</p>
          </div>
        </div>
        <div class="metrics">
          <div class="metric"><div class="metric-label">Included SKU Rows</div><div class="metric-value">${escapeHtml(formatUpcomingBuyNumber(rows.length))}</div></div>
          <div class="metric"><div class="metric-label">Units to Order</div><div class="metric-value">${escapeHtml(totalUnitsToOrder)}</div></div>
          <div class="metric"><div class="metric-label">Order Value</div><div class="metric-value">${escapeHtml(totalOrderValue)}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th class="col-sku">Supply SKU</th>
              <th class="col-product">Product Name</th>
              <th class="col-units num">Units to Order</th>
              <th class="col-cost num">Unit Cost</th>
              <th class="col-value num">Order Value</th>
              <th class="col-po">PO Planned Date</th>
              <th class="col-arrival">Expected Arrival</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </main>
      <script>
        const readyToPrint = () => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              window.focus();
              window.print();
              setTimeout(() => window.close(), 250);
            }, 300);
          });
        };

        if (document.readyState === "complete") {
          readyToPrint();
        } else {
          window.addEventListener("load", readyToPrint, { once: true });
        }
      </script>
    </body>
  </html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
}

function emailUpcomingBuySupplierDraft(supplierName, selectedMonthLabel, rows) {
    const subject = `${supplierName} upcoming buys · ${selectedMonthLabel}`;
    const body = [
        `Hello ${supplierName},`,
        "",
        `Please review the upcoming buys planned for ${selectedMonthLabel}.`,
        "",
        `SKU rows: ${formatUpcomingBuyNumber(rows.length)}`,
        `Units to order: ${formatUpcomingBuyNumber(rows.reduce((sum, row) => sum + Number(row.unitsToOrder || 0), 0))}`,
        `Order value: ${formatUpcomingBuyCurrency(rows.reduce((sum, row) => sum + Number(row.orderValue || 0), 0))}`,
        `Deposit liability: ${formatUpcomingBuyCurrency(rows.reduce((sum, row) => sum + Number(row.depositLiability || 0), 0))}`,
        "",
        "Please reply with any questions or confirmations.",
    ].join("\n");

    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function normalizeUpcomingBuyRow(row) {
    const unitsToOrder = Number(row.unitsToOrder ?? 0);
    const unitCost = Number(row.unitCost ?? 0);
    const orderValue = Number(row.orderValue ?? row.cashValue ?? unitsToOrder * unitCost ?? 0);
    const leadTimeDays = Number(row.leadTimeDays ?? 60);
    const poPlannedDate = row.poPlannedDate || row.buyDate || null;

    return {
        ...row,
        buyUnits: Number(row.buyUnits ?? unitsToOrder),
        unitsToOrder,
        unitCost,
        orderValue,
        depositLiability: Number(row.depositLiability ?? orderValue * 0.15),
        leadTimeDays,
        poPlannedDate,
        expectedArrivalDate:
            row.expectedArrivalDate || addDaysToIsoDate(poPlannedDate, leadTimeDays) || poPlannedDate,
        status: row.status || "Active",
    };
}

function resolveDefaultBuyMonth(availableBuyMonths, today = new Date()) {
    if (!Array.isArray(availableBuyMonths) || availableBuyMonths.length === 0) return null;
    const currentMonthKey = getMonthKey(today.toISOString().slice(0, 10));
    if (currentMonthKey) {
        const currentOrNext = availableBuyMonths.find((monthKey) => monthKey >= currentMonthKey);
        if (currentOrNext) return currentOrNext;
    }
    return availableBuyMonths[0];
}

function buildUpcomingBuySupplierGroups(rows) {
    const grouped = new Map();

    (rows || []).forEach((row) => {
        const current = grouped.get(row.supplier);
        if (current) {
            current.rowCount += 1;
            current.buyUnits += Number(row.buyUnits || 0);
            current.unitsToOrder += Number(row.unitsToOrder || 0);
            current.orderValue += Number(row.orderValue || 0);
            current.depositLiability += Number(row.depositLiability || 0);
            current.earliestPlannedDate =
                current.earliestPlannedDate <= row.poPlannedDate
                    ? current.earliestPlannedDate
                    : row.poPlannedDate;
            current.rows.push(row);
            return;
        }

        grouped.set(row.supplier, {
            supplier: row.supplier,
            rowCount: 1,
            buyUnits: Number(row.buyUnits || 0),
            unitsToOrder: Number(row.unitsToOrder || 0),
            orderValue: Number(row.orderValue || 0),
            depositLiability: Number(row.depositLiability || 0),
            earliestPlannedDate: row.poPlannedDate,
            rows: [row],
        });
    });

    return Array.from(grouped.values()).sort((left, right) => {
        return (
            String(left.earliestPlannedDate || "").localeCompare(String(right.earliestPlannedDate || "")) ||
            right.orderValue - left.orderValue ||
            String(left.supplier || "").localeCompare(String(right.supplier || ""))
        );
    });
}

function buildUpcomingBuySummary(rows) {
    const buyUnits = (rows || []).reduce((sum, row) => sum + Number(row.buyUnits || 0), 0);
    const totalUnits = (rows || []).reduce((sum, row) => sum + Number(row.unitsToOrder || 0), 0);
    const orderValue = (rows || []).reduce((sum, row) => sum + Number(row.orderValue || 0), 0);
    const depositLiability = (rows || []).reduce(
        (sum, row) => sum + Number(row.depositLiability || 0),
        0
    );
    const supplierCount = new Set((rows || []).map((row) => row.supplier)).size;
    const earliestPlannedDate = rows?.[0]?.poPlannedDate ?? null;

    return {
        rowCount: rows?.length ?? 0,
        buyUnits,
        totalUnits,
        orderValue,
        depositLiability,
        totalCash: orderValue,
        supplierCount,
        earliestPlannedDate,
        earliestDate: earliestPlannedDate,
    };
}

function buildUpcomingBuyMonthOptions(availableBuyMonths) {
    return (availableBuyMonths || []).map((monthKey) => ({
        value: monthKey,
        label: formatUpcomingBuyMonthLabel(monthKey),
    }));
}

module.exports = {
    ACTIVE,
    IN_ACTIVE,
    SUNSET,
    PLANNED,
    enumUpcomingBuyProductStatus,
    EADIN_BUY_MONTHS,
    parseIsoDate,
    getMonthKey,
    isEadinBuyMonth,
    resolveEadinBuyMonthKey,
    formatUpcomingBuyDate,
    formatUpcomingBuyMonthLabel,
    formatUpcomingBuyCurrency,
    formatUpcomingBuyNumber,
    getUpcomingBuyStatusLabel,
    getUpcomingBuyStatusTone,
    getBuyOverrideTone,
    addDaysToIsoDate,
    slugifyUpcomingBuySupplier,
    downloadUpcomingBuysCsv,
    downloadUpcomingBuysPdf,
    emailUpcomingBuySupplierDraft,
    normalizeUpcomingBuyRow,
    resolveDefaultBuyMonth,
    buildUpcomingBuySupplierGroups,
    buildUpcomingBuySummary,
    buildUpcomingBuyMonthOptions,
};
